/**
 * What a file item's state is painted on: `self` — the item itself, hovered
 * directly, over its own white fill (a list row); `group` — a clear part of the
 * `group/attachment` element, lit while anywhere on that element is hovered (a
 * tile's caption, under its picture).
 */
export type AttachmentHoverTarget = "self" | "group";

// Whole literals, so Tailwind's scanner finds them. A row's tint is a flat
// gradient laid over its `bg-bg-light` rather than a background color in its
// place, so the translucent tokens never leave the card see-through.
//
// The hover tint also holds while the item's "…" menu is open: the menu is
// portalled, so moving onto it ends the item's :hover. The open trigger is
// matched by `aria-expanded`, not `data-state` — a Tooltip wrapped around a
// trigger overwrites its `data-state` with its own.
const STATE_BACKGROUND: Record<
  AttachmentHoverTarget,
  { selected: string; hovered: string }
> = {
  self: {
    selected: "bg-linear-to-r from-bg-primary-min to-bg-primary-min",
    hovered:
      "hover:bg-linear-to-r hover:from-bg-dark-min hover:to-bg-dark-min has-[[aria-expanded=true]]:bg-linear-to-r has-[[aria-expanded=true]]:from-bg-dark-min has-[[aria-expanded=true]]:to-bg-dark-min",
  },
  group: {
    selected: "bg-bg-primary-min",
    hovered:
      "group-hover/attachment:bg-bg-dark-min group-has-[[aria-expanded=true]]/attachment:bg-bg-dark-min",
  },
};

/**
 * The background a file item takes (designs `CardFileSmall` and `ItemListFile`):
 * the primary tint while it is selected, else a quiet grey while it is hovered
 * or its menu is open.
 */
export function attachmentStateBackground(
  selected: boolean,
  hoverTarget: AttachmentHoverTarget,
): string {
  const background = STATE_BACKGROUND[hoverTarget];
  return selected ? background.selected : background.hovered;
}
